import { FormProvider, useForm } from 'react-hook-form'
import Button from '@ancon/wildcat-ui/web/orderweb/Button'
import useTranslation from 'next-translate/useTranslation'
import Alert from '@ancon/wildcat-ui/web/orderweb/Alert'
import { useState } from 'react'

import { LogInFormState } from '../../../auth/types'
import LoginFormInputs from '../../../auth/forms/LoginFormInputs'
import useAppDispatch from '../../../../store/hooks/useAppDispatch'
import { linkCredentialsToGuestUser } from '../../../auth/store/authThunks'
import getTranslatedError from '../../../app/utils/getTranslatedError'
import appLanguage from '../../../app/utils/appLanguage'
import useAppSelector from '../../../../store/hooks/useAppSelector'
import {
  preOrderIsAnonymousAttendeeSelector,
  preOrderUserEmailSelector,
} from '../../../preOrder/store/preOrderSelectors'

import styles from './GuestCustomerOnboardingForm.module.scss'

type OrderStatusCreateAccountFormProps = {
  onClickClose: () => void
}

export default function OrderStatusCreateAccountForm({
  onClickClose,
}: OrderStatusCreateAccountFormProps) {
  const { t } = useTranslation('common')
  const dispatch = useAppDispatch()

  const [accountLinkError, setAccountLinkError] = useState<string | null>(null)

  const preOrderUserEmail = useAppSelector(preOrderUserEmailSelector)
  const preOrderIsAnonymousAttendee = useAppSelector(
    preOrderIsAnonymousAttendeeSelector,
  )

  const form = useForm<LogInFormState>({
    mode: 'onChange',
    defaultValues: {
      email: preOrderIsAnonymousAttendee ? preOrderUserEmail : '',
      password: '',
    },
  })

  const {
    formState: { isSubmitting },
    handleSubmit,
  } = form

  async function handleOnClickCreateAccount(values: LogInFormState) {
    const { email, password } = values

    try {
      await dispatch(
        linkCredentialsToGuestUser({
          email: email.toLowerCase(),
          password,
        }),
      ).unwrap()
      onClickClose()
    } catch (err) {
      const locale = appLanguage.get()
      const translatedError = await getTranslatedError(err, undefined, locale)

      setAccountLinkError(translatedError?.message || translatedError?.title)
    }
  }

  function clearAccountLinkError() {
    setAccountLinkError(null)
  }

  return (
    <FormProvider
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...form}
    >
      <Alert
        className={styles.alert}
        isOpen={!!accountLinkError}
        toggle={clearAccountLinkError}
        type="danger"
      >
        {accountLinkError}
      </Alert>
      <LoginFormInputs
        readonlyEmail={preOrderIsAnonymousAttendee && !!preOrderUserEmail}
      />
      <div className={styles.modalFooter}>
        <Button variant="secondary" onClick={onClickClose}>
          {t('components.createAccountModal.cancel')}
        </Button>
        <Button
          onClick={handleSubmit(handleOnClickCreateAccount)}
          loading={isSubmitting}
        >
          {t('components.createAccountModal.signUp')}
        </Button>
      </div>
    </FormProvider>
  )
}
